import { updateWebsite } from '@va/dashboard/actions/api';
import { getFirstWeekDay } from '@va/dashboard/selectors/core';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { SelectDropdownOption } from '@va/ui/design-system';
import { isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useChangeFirstWeekDay } from '../apiClient';

const useFirstWeekDayCard = () => {
  const currentFirstWeekDay = useSelector(getFirstWeekDay);

  const [selectedDay, setSelectedDay] = useState<SelectDropdownOption<number> | null>(null);

  const { execute: changeFirstWeekDay, error, isSucceeded, clearError } = useChangeFirstWeekDay();

  const { showSuccessNotification, showErrorNotification } = useAddNotification();

  const translate = useTranslate();
  const dispatch = useDispatch();

  // Update the website state after the api call is succeeded
  useEffect(() => {
    if (!isSucceeded) return;
    showSuccessNotification(2000);
    dispatch(updateWebsite({ firstWeekDay: selectedDay?.value }));
  }, [dispatch, isSucceeded, selectedDay?.value, showSuccessNotification]);

  // Show error notification and change to previous state
  useEffect(() => {
    if (!error || currentFirstWeekDay === null) return;
    clearError();
    showErrorNotification(2000);
    setSelectedDay({
      value: currentFirstWeekDay,
      label: getOptionLabel(currentFirstWeekDay, translate),
    });
  }, [error, clearError, dispatch, currentFirstWeekDay, showErrorNotification, translate]);

  // Populating the initial state
  useEffect(() => {
    if (isNil(currentFirstWeekDay)) return;
    setSelectedDay({
      value: currentFirstWeekDay,
      label: getOptionLabel(currentFirstWeekDay, translate),
    });
  }, [currentFirstWeekDay, translate]);

  const onChange = useCallback(
    (option: SelectDropdownOption<number>) => {
      setSelectedDay(option);
      changeFirstWeekDay(option.value);
    },
    [changeFirstWeekDay],
  );

  return {
    onChange,
    selectedDay,
  };
};

export default useFirstWeekDayCard;

export function getDropdownOptions(translate: any) {
  return [
    {
      value: 1,
      label: translate('labels.days.monday'),
    },
    {
      value: 2,
      label: translate('labels.days.tuesday'),
    },
    {
      value: 3,
      label: translate('labels.days.wednesday'),
    },
    {
      value: 4,
      label: translate('labels.days.thursday'),
    },
    {
      value: 5,
      label: translate('labels.days.friday'),
    },
    {
      value: 6,
      label: translate('labels.days.saturday'),
    },
    {
      value: 0,
      label: translate('labels.days.sunday'),
    },
  ];
}

function getOptionLabel(day: number, translate: any) {
  switch (day) {
    case 1:
      return translate('labels.days.monday');
    case 2:
      return translate('labels.days.tuesday');
    case 3:
      return translate('labels.days.wednesday');
    case 4:
      return translate('labels.days.thursday');
    case 5:
      return translate('labels.days.friday');
    case 6:
      return translate('labels.days.saturday');
    case 0:
      return translate('labels.days.sunday');
    default:
      return null;
  }
}
